import { FeatureFlags, MainRouteUrls, ProfessionalBaseType } from '@domain/types';
import { getUserInitials, isLifenInternalEmail } from '@domain/utils';
import { Tooltip } from '@honestica/design-system';
import ChatIcon from '@honestica/ui-kit/assets/icons/Chat.svg?react';
import ChatFilledIcon from '@honestica/ui-kit/assets/icons/ChatFilled.svg?react';
import CheckedIcon from '@honestica/ui-kit/assets/icons/Checked.svg?react';
import HelpIcon from '@honestica/ui-kit/assets/icons/Help.svg?react';
import HelpFilledIcon from '@honestica/ui-kit/assets/icons/HelpFilled.svg?react';
import HomeIcon from '@honestica/ui-kit/assets/icons/Home.svg?react';
import HomeFilledIcon from '@honestica/ui-kit/assets/icons/HomeFilled.svg?react';
import SettingIcon from '@honestica/ui-kit/assets/icons/Setting.svg?react';
import SettingFilledIcon from '@honestica/ui-kit/assets/icons/SettingFilled.svg?react';
import WorkspaceIcon from '@honestica/ui-kit/assets/icons/Workspace.svg?react';
import WorkspaceFilledIcon from '@honestica/ui-kit/assets/icons/WorkspaceFilled.svg?react';
import { Avatar, colors, Menu, Space } from '@honestica/ui-kit/src';
import { ModalType, useModal } from '@hooks/useModal.hook';
import { useZendeskChat } from '@hooks/useZendeskChat.hook';
import { useFeatureFlags } from '@launcher/context/FeatureFlags.context';
import { useCustomNavigate } from '@launcher/hooks/useCustomNavigate.hook';
import { isDesktopApp, openLink, openSettingsLink } from '@launcher/utils';
import { EnvManager } from '@services/environment/environment.service';
import { logger } from '@services/logger/logger.service';
import { selectDesktopSettings } from '@store/desktop/desktop.selector';
import { logout, switchWorkspace } from '@store/user/user.actions';
import { getIsLifenSendingActivated, getUser } from '@store/user/user.selector';
import type { MenuProps } from 'antd';
import cx from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './SubMenus.module.less';

export const SubMenus = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openZendeskChat } = useZendeskChat();
  const { openModal } = useModal();

  const navigate = useCustomNavigate();
  const hideSupport = useSelector(selectDesktopSettings).hideSupport;
  const user = useSelector(getUser);
  const lifenSendingActivated = useSelector(getIsLifenSendingActivated);

  const lifenIntegrationActivated = user.identities.identities.some((identity) => {
    const hasActiveIntegrationsModule = Boolean(identity.featureSets?.lifenFeatureSetIntegrations);

    return identity.type === ProfessionalBaseType.Organization && hasActiveIntegrationsModule;
  });

  const isQA = useMemo(
    (): boolean =>
      isLifenInternalEmail(user.email) &&
      ['staging', 'dev', 'review'].includes(EnvManager.getName() ?? ''),
    [user.email],
  );

  const userInitials = useMemo(() => getUserInitials(user), [user]);

  const openZendesk = () => {
    try {
      openZendeskChat();
    } catch (error) {
      logger.error('UI', 'Failed to display the Zendesk Chat', error);
    }
  };

  const handleSwitchWorkspace = (workspaceId: string) => {
    if (workspaceId !== user.currentWorkspaceId) {
      dispatch(switchWorkspace(workspaceId));
    }
  };

  const featureFlags: FeatureFlags = useFeatureFlags();

  const createWorkspaceMenuItems = useMemo(() => {
    if (user.workspaces.length <= 1) return [];

    return user.workspaces.map((workspace) => {
      const isCurrent = workspace.id === user.currentWorkspaceId;
      return {
        key: workspace.id,
        label: (
          <Space fullWidth>
            <div className={styles.workspaces}>
              {isCurrent ? <HomeFilledIcon /> : <HomeIcon />}
              <span className={cx({ [styles.workspaceSelected]: isCurrent })}>
                {workspace.name}
              </span>
              {isCurrent && <CheckedIcon />}
            </div>
          </Space>
        ),
        onClick: () => handleSwitchWorkspace(workspace.id),
      };
    });
  }, [user.workspaces, user.currentWorkspaceId, handleSwitchWorkspace]);

  const createHelpMenuItems = useMemo(
    () => [
      {
        key: 'help-section',
        label: t('help.helpSection'),
        onClick: () => openLink(t('help.helpSectionURL')),
        'data-testid': 'help-content',
      },
      {
        key: 'training-videos',
        label: t('help.trainingVideos'),
        onClick: () => openLink(t('help.trainingVideosURL')),
        'data-testid': 'training-videos',
      },
      ...(!hideSupport
        ? [
            {
              key: 'intercom',
              label: t('help.helpByChat'),
              onClick: openZendesk,
              'data-testid': 'help-by-chat',
            },
          ]
        : []),
      ...(lifenSendingActivated
        ? [
            {
              key: 'news-lifen-sending',
              label: t('help.news.sending.title'),
              onClick: () => openLink(t('help.news.sending.link')),
              'data-testid': 'news-lifen-sending',
            },
          ]
        : []),
      ...(lifenIntegrationActivated
        ? [
            {
              key: 'news-lifen-integration',
              label: t('help.news.integration.title'),
              onClick: () => openLink(t('help.news.integration.link')),
              'data-testid': 'news-lifen-integration',
            },
          ]
        : []),
      {
        key: 'about',
        label: t('help.about'),
        onClick: () => openModal({ modal: ModalType.helpAbout }),
        'data-testid': 'about',
      },
      {
        key: 'system-status',
        label: t('help.systemStatus'),
        onClick: () => openLink(t('help.systemStatusURL')),
        'data-testid': 'system-status',
      },
    ],
    [
      t,
      hideSupport,
      lifenSendingActivated,
      lifenIntegrationActivated,
      featureFlags,
      openLink,
      openZendesk,
      openModal,
    ],
  );

  const createSettingsMenuItems = useMemo(
    () => [
      {
        key: 'user-info-group',
        type: 'group',
        label: (
          <div>
            <Space className={styles.userEmail}>
              <Avatar background={colors.primary[5]} color={colors.white[0]}>
                {userInitials}
              </Avatar>
              <strong>{user.email}</strong>
            </Space>
          </div>
        ),
        children: [
          {
            type: 'divider',
          },
          {
            key: 'profile',
            label: t('settings.parameters.profile'),
            onClick: () => openSettingsLink({ url: EnvManager.getBaseUrlUm() || '' }),
          },
          ...(isDesktopApp()
            ? [
                {
                  key: 'desktopSettings',
                  label: t('settings.desktop'),
                  onClick: () => navigate(`/${MainRouteUrls.DesktopSettings}`),
                },
              ]
            : []),
          ...(isQA
            ? [
                {
                  key: 'qa',
                  label: t('settings.parameters.qa.title'),
                  onClick: () => openModal({ modal: ModalType.settingsQA }),
                },
              ]
            : []),
          {
            key: 'logout',
            label: t('logout'),
            onClick: () => dispatch(logout),
          },
          ...(isDesktopApp()
            ? [
                {
                  key: 'exit',
                  label: t('exit'),
                  onClick: () => window.electron.exitApp(),
                },
              ]
            : []),
        ],
      },
    ],
    [t, user.email, userInitials, isQA, openSettingsLink, navigate, openModal, dispatch],
  );

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'chat',
        icon: (
          <Tooltip placement="right" content={t('menuChat')}>
            <span role="img" aria-label="chat" className="anticon">
              <ChatIcon className={styles.icon} />
              <ChatFilledIcon className={styles.iconFilled} />
            </span>
          </Tooltip>
        ),
        label: '',
        title: t('menuChat'),
        className: styles.menuItem,
        onClick: () => openZendesk(),
        'data-testid': 'sidebar-menu-chat',
      },
      {
        key: 'workspaces',
        icon: (
          <span role="img" aria-label="workspaces" className="anticon">
            <WorkspaceIcon className={styles.icon} />
            <WorkspaceFilledIcon className={styles.iconFilled} />
          </span>
        ),
        label: '',
        children: createWorkspaceMenuItems,
        className: styles.menuItem,
        'data-testid': 'workspace-switch',
      },
      {
        key: 'help',
        icon: (
          <span role="img" aria-label="help" className="anticon">
            <HelpIcon className={styles.icon} />
            <HelpFilledIcon className={styles.iconFilled} />
          </span>
        ),
        label: '',
        className: styles.menuItem,
        children: createHelpMenuItems,
        'data-testid': 'help-menu',
      },
      {
        key: 'settings',
        icon: (
          <span role="img" aria-label="settings" className="anticon">
            <SettingIcon className={styles.icon} />
            <SettingFilledIcon className={styles.iconFilled} />
          </span>
        ),
        label: '',
        className: styles.menuItem,
        children: createSettingsMenuItems,
        'data-testid': 'settings-menu',
      },
    ],
    [t, openZendesk, createWorkspaceMenuItems, createHelpMenuItems, createSettingsMenuItems],
  );

  return (
    <div className={styles.subMenus}>
      <Menu mode="vertical" style={{ width: 64 }} items={menuItems} />
    </div>
  );
};
